import defaultTheme from '@oberoninternal/travelbase-ds/themes/default';
import websiteTheme from './website';
import { FontStyle, Theme, Colors } from '@oberoninternal/travelbase-ds/constants/theme';
import { BodyVariant } from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { TitleVariant } from '@oberoninternal/travelbase-ds/components/primitive/Title';

/**
 * The Qualitylodgings Voorkant theme.
 * The Qualitylodgings theme for the booking site.
 */

const primary = {
    0: '#FFFFFF',
    5: '#FFFFFF',
    10: '#FFFFFF',
    20: '#FFFFFF',
    30: '#FFFFFF',
    40: '#FFFFFF',
    50: '#000000',
    60: '#000000',
    70: '#000000',
    80: '#000000',
    90: '#000000',
    100: '#000000',
};

const colors: Colors = {
    primary,
    tertiary: {
        0: '#D9D9D6',
        5: '#D9D9D6',
        10: '#D9D9D6',
        20: '#D9D9D6',
        30: '#D9D9D6',
        40: '#D9D9D6',
        50: '#75787B',
        60: '#75787B',
        70: '#75787B',
        80: '#75787B',
        90: '#75787B',
        100: '#75787B',
    },
    secondary: {
        0: '#789F90',
        5: '#789F90',
        10: '#789F90',
        20: '#789F90',
        30: '#789F90',
        40: '#789F90',
        50: '#789F90',
        60: '#789F90',
        70: '#789F90',
        80: '#789F90',
        90: '#789F90',
        100: '#789F90',
    },
    neutral: defaultTheme.colors.neutral,
    positive: defaultTheme.colors.positive,
    warning: defaultTheme.colors.warning,
    negative: defaultTheme.colors.negative,
};

const fontFamily: Theme['fontFamily'] = {
    body: 'Gilroy,sans-serif',
    title: 'Times New Roman,sans-serif',
};

const baseTitle: Partial<FontStyle> = {
    color: colors.primary[100],
};

const titleStyles: Record<TitleVariant, FontStyle> = {
    large: {
        size: { min: 24, max: 32 },
        line: { min: 24, max: 32 },
        weight: 700,
        letterSpacing: 0,
        ...baseTitle,
    },
    regular: {
        size: { min: 16, max: 18 },
        line: { min: 20, max: 24 },
        weight: 700,
        letterSpacing: 0,
        ...baseTitle,
    },
    small: {
        size: { min: 16, max: 18 },
        line: { min: 20, max: 24 },
        weight: 400,
        ...baseTitle,
    },
    tiny: {
        size: { min: 15, max: 16 },
        line: { min: 20, max: 20 },
        weight: 700,
        ...baseTitle,
    },
    micro: {
        size: { min: 14, max: 15 },
        line: { min: 15, max: 20 },
        weight: 700,
        ...baseTitle,
    },
};

const bodyStyles: Record<BodyVariant, FontStyle> = {
    huge: {
        size: { min: 14, max: 18 },
        line: { min: 24, max: 28 },
        weight: 400,
    },
    large: {
        size: { min: 16, max: 20 },
        line: { min: 24, max: 28 },
        weight: 400,
    },
    regular: {
        size: { min: 14, max: 18 },
        line: { min: 24, max: 28 },
        weight: 400,
    },
    small: {
        size: { min: 13, max: 14 },
        line: { min: 20, max: 20 },
        letterSpacing: 0,
        weight: 400,
    },
    tiny: {
        size: { min: 12, max: 13 },
        line: { min: 20, max: 20 },
        letterSpacing: 0,
        weight: 400,
    },
    micro: {
        size: { min: 12, max: 12 },
        line: { min: 20, max: 20 },
        letterSpacing: 0,
        weight: 400,
    },
};

const qualitylodgings: Theme = {
    ...websiteTheme,
    name: 'Qualitylodgings',
    fontFamily,
    fontSizes: {
        title: titleStyles,
        body: bodyStyles,
    },
    colors,
    colorAssignments: {
        main: ['primary', 60],
        buttonPrimary: ['secondary', 60],
        buttonLight: ['secondary', 30],
        textAction: ['secondary', 60],
        input: ['secondary', 50],
        form: ['secondary', 50],
        rating: ['secondary', 40],
        booking: ['secondary', 50],
        special: ['primary', 40],
        calendar: {
            defaultBackgroundColor: ['primary', 0],
            defaultColor: ['neutral', 100],
            bookable: {
                color: ['neutral', 100],
                backgroundColor: '#EBF9F4',
                focusColor: '#1F7457',
            },
            selected: {
                backgroundColor: '#33C191',
                color: ['neutral', 0],
                focusColor: '#33C191',
            },
            bookableOutside: ['neutral', 100],
            bookableWithin: {
                selected: ['neutral', 100],
                notSelected: ['neutral', 0],
            },
            periodBackground: {
                selected: '#EBF9F4',
                notSelected: '#33C191',
            },
            withinCss: {
                selected: '#33C191',
                notSelected: ['neutral', 0],
            },
            price: '#1F7457',
        },
        tagButton: {
            color: ['neutral', 0],
            backgroundColor: ['secondary', 60],
            borderColor: ['secondary', 60],
        },
    },
    radius: {
        button: '0',
        textArea: '0',
        textInput: '0',
        dropdown: '0.5rem',
    },
};

export default qualitylodgings;
